<template>
  <v-form ref="form">
    <div class="credential-set-input-container">
      <div class='settings-field' v-if='isFirstInputIsJsonField'>
        <span class='label'>{{ firstLabelText }}</span>
        <div class='json-input-area'>
          <v-textarea
            v-if='!previewJson'
            class='text-field'
            outlined
            no-resize
            v-model='jsonHiddenValue'
            v-click-outside="onTextAreaClicked"
          />
          <div v-else @click="onTextAreaClicked" class='json-pretty-container'>
            <vue-json-pretty :data='jsonFormattedValue' />
          </div>
        </div>
      </div>
      <div class='settings-field' v-else>
        <span class='label'>{{ firstLabelText }}</span>
        <text-field class='text-field'
                    :placeholder='firstLabelText'
                    v-model='value.first'
                    :type="firstLabelType"
        />
      </div>
      <div class='settings-field' v-if='secondLabelText'>
        <span class='label'>{{ secondLabelText }}</span>
        <text-field class='text-field'
          :placeholder='secondLabelText'
          v-model='value.second'
          :type="secondLabelType"
        />
      </div>
      <div class='settings-field' v-if='thirdLabelText'>
        <span class='label'>{{ thirdLabelText }}</span>
        <text-field class='text-field'
          :placeholder='thirdLabelText'
          v-model='value.third'
          :type="thirdLabelType"
        />
      </div>
    </div>
  </v-form>
</template>

<script>
import TextField from '@/components/common/TextField.vue';
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import vClickOutside from 'v-click-outside';
import { cloneDeep } from 'lodash';

export const defaultHiddenJsonAuthObject = {
  type: '**********',
  project_id: '**********',
  private_key_id: '********************',
  client_email: '**********',
  client_id: '**********',
  auth_uri: 'https://accounts.google.com/o/oauth2/auth',
  token_uri: 'https://oauth2.googleapis.com/token',
  auth_provider_x509_cert_url: '**********',
  client_x509_cert_url: '**********',
};

export default {
  name: 'CredentialSetInput',
  components: { TextField, VueJsonPretty },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    // PASSING THE PROP -VISIBLE (BELONGS TO POPUP), TO RESET THE DATA EACH TIME IT IS CHANGED
    visible: {
      type: Boolean,
      required: true,
    },
    value: Object,
    firstInput: {
      type: Object,
      required: true,
    },
    secondInput: {
      type: Object,
      required: false,
    },
    thirdInput: {
      type: Object,
      required: false,
    },
    isTranslated: {
      type: Boolean,
      required: false,
      default: false,
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    visible() {
      this.previewJson = this.editMode;
      this.editStarted = false;
      this.previewJsonValueForEditMode = cloneDeep(defaultHiddenJsonAuthObject);
    },
  },
  data() {
    return {
      previewJson: this.editMode,
      editStarted: false,
      previewJsonValueForEditMode: cloneDeep(defaultHiddenJsonAuthObject),
    };
  },
  methods: {
    onTextAreaClicked() {
      if (this.previewJson) {
        if (this.editMode) {
          if (!this.editStarted) {
            this.value.first = '';
            this.editStarted = true;
          }
        }
        this.previewJson = false;
      } else if (!this.jsonPreviewDisabled) {
        this.previewJson = true;
      } else {
        this.previewJson = false;
      }
    },
  },
  computed: {
    jsonHiddenValue: {
      get() {
        if (!this.editMode || this.editStarted) {
          return this.value.first;
        }
        return JSON.stringify(this.previewJsonValueForEditMode);
      },
      set(val) {
        this.value.first = val;
      },
    },
    jsonFormattedValue() {
      let value = '';
      try {
        if (!this.editMode || this.editStarted) {
          value = JSON.parse(this.value.first);
        } else {
          value = this.previewJsonValueForEditMode;
        }
      } catch (e) {
        console.log(e);
      }
      return value;
    },
    jsonPreviewDisabled() {
      return !this.value.first.length || this.jsonFormattedValue === '';
    },
    isFirstInputIsJsonField() {
      return this.firstLabelType === 'jsonField';
    },
    firstLabelType() {
      return this.firstInput.type.toString();
    },
    secondLabelType() {
      return this.secondInput.label ? this.secondInput.type.toString() : '';
    },
    thirdLabelType() {
      return this.thirdInput.label ? this.thirdInput.type.toString() : '';
    },
    firstLabelText() {
      return this.isTranslated ? this.firstInput.label : this.$t(this.firstInput.label);
    },
    secondLabelText() {
      if (this.secondInput?.label) {
        return this.isTranslated ? this.secondInput.label : this.$t(this.secondInput.label);
      }
      return '';
    },
    thirdLabelText() {
      if (this.thirdInput?.label) {
        return this.isTranslated ? this.thirdInput.label : this.$t(this.thirdInput.label);
      }
      return '';
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.credential-set-input-container {
  .json-input-area {
    .vjs-tree {
      min-width: rem(380);
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.credential-set-input-container {
  margin-top: $space-2;

  .settings-field {
    margin-bottom: $space-2;

    &.json-field {
      width: rem(582);
    }

    .json-input-area {
      display: flex;

      .preview-btn {
        margin-left: $space-2;
        margin-top: $space-1;
        align-self: start;
        color: var(--text-secondary);
        font-size: $font-size-md;
        letter-spacing: 0;

        &:hover {
          background-color: var(--bg-tertiary);
          cursor: pointer;
        }

        &.disabled {
          opacity: 0.5;
          cursor: default;
        }
      }
    }
  }

  .text-field {
    margin-top: $space-1 !important;
  }

  .label {
    text-transform: uppercase;
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-xs;
    letter-spacing: 0.8px;
    line-height: rem(10);
  }
}
</style>
